import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
  useMediaQuery,
  useTheme,
  AppBar,
  IconButton,
  Breadcrumbs,
  Link,
  Avatar,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { nodeApi } from '../../../config/apiClient';

// API Base URL (Node.js sunucunuzun adresine göre güncelleyin)
// Ortama göre otomatik API URL belirleme
const API_BASE_URL = (() => {
  // Tarayıcı URL'sini kontrol et
  const currentUrl = window.location.href;
  
  // Eğer localhost, 127.0.0.1 veya local IP ise geliştirme ortamındayız
  if (currentUrl.includes('localhost') || currentUrl.includes('127.0.0.1') || /192\.168\.\d+\.\d+/.test(currentUrl)) {
    return 'http://localhost:3001/api'; // Geliştirme ortamı için HTTP
  }
  
  // Aksi halde canlı ortamdayız, HTTPS kullanmalıyız
  return 'https://gtsapi.mt-teknoloji.com/api'; // Canlı ortam için HTTPS
})();

// LocalStorage için sabit anahtarlar
const IMAGE_CACHE_PREFIX = 'qr_menu_img_';
const IMAGE_CACHE_TIMESTAMP_PREFIX = 'qr_menu_img_timestamp_';
const CACHE_VALIDITY_MS = 24 * 60 * 60 * 1000; // 24 saat

// QR Menü verileri için önbellek anahtarları
const MENU_CACHE_PREFIX = 'qr_menu_data_';
const MENU_CACHE_TIMESTAMP_PREFIX = 'qr_menu_data_timestamp_';
const MENU_CACHE_VALIDITY_MS = 4 * 60 * 60 * 1000; // 4 saat

// Performans ölçüm logları için yardımcı fonksiyon
const logPerformance = (message: string) => {
  console.log(`[PERFORMANCE] ${new Date().toISOString()} - ${message}`);
};

// Resim önbellekleme için yardımcı fonksiyonlar - karakter sınırını aşmamak için basit tutuyoruz
const getCacheKey = (url: string): string => {
  return `${IMAGE_CACHE_PREFIX}${url.replace(/[^a-zA-Z0-9]/g, '_')}`;
};

const getTimestampKey = (url: string): string => {
  return `${IMAGE_CACHE_TIMESTAMP_PREFIX}${url.replace(/[^a-zA-Z0-9]/g, '_')}`;
};

// API verileri için önbellek yardımcı fonksiyonları
const getMenuCacheKey = (menuId: string): string => {
  return `${MENU_CACHE_PREFIX}${menuId}`;
};

const getMenuTimestampKey = (menuId: string): string => {
  return `${MENU_CACHE_TIMESTAMP_PREFIX}${menuId}`;
};

const saveImageToCache = (url: string, cachedUrl: string) => {
  try {
    localStorage.setItem(getCacheKey(url), cachedUrl);
    localStorage.setItem(getTimestampKey(url), Date.now().toString());
  } catch (error) {
    console.error('Resim önbelleğe kaydedilemedi:', error);
  }
};

const getImageFromCache = (url: string): { data: string | null, isValid: boolean } => {
  try {
    const cachedImage = localStorage.getItem(getCacheKey(url));
    const timestamp = localStorage.getItem(getTimestampKey(url));
    
    if (!cachedImage || !timestamp) {
      return { data: null, isValid: false };
    }
    
    const age = Date.now() - parseInt(timestamp);
    return { 
      data: cachedImage, 
      isValid: age < CACHE_VALIDITY_MS 
    };
  } catch (error) {
    console.error('Resim önbellekten alınamadı:', error);
    return { data: null, isValid: false };
  }
};

const saveMenuToCache = (menuId: string, menuData: any) => {
  try {
    localStorage.setItem(getMenuCacheKey(menuId), JSON.stringify(menuData));
    localStorage.setItem(getMenuTimestampKey(menuId), Date.now().toString());
    logPerformance(`Menü verileri önbelleğe kaydedildi: ${menuId}`);
  } catch (error) {
    console.error('Menü verileri önbelleğe kaydedilemedi:', error);
  }
};

const getMenuFromCache = (menuId: string): { data: any | null, isValid: boolean } => {
  try {
    const cachedData = localStorage.getItem(getMenuCacheKey(menuId));
    const timestamp = localStorage.getItem(getMenuTimestampKey(menuId));
    
    if (!cachedData || !timestamp) {
      return { data: null, isValid: false };
    }
    
    const age = Date.now() - parseInt(timestamp);
    return { 
      data: JSON.parse(cachedData), 
      isValid: age < MENU_CACHE_VALIDITY_MS 
    };
  } catch (error) {
    console.error('Menü verileri önbellekten alınamadı:', error);
    return { data: null, isValid: false };
  }
};

// Lazy loading için IntersectionObserver yardımcı fonksiyonu
const useIntersectionObserver = (ref: React.RefObject<HTMLElement | null>, options: IntersectionObserverInit = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [ref, options]);

  return isIntersecting;
};

// Ürün veri tipi
interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  cachedImageUrl?: string; // Önbellekten gelen resim URL'si
  categoryId?: string | null;
  ingredients?: string[];
  isAvailable?: boolean;
  options?: {
    [key: string]: {
      name: string;
      price?: number;
    }[]
  };
}

// QR Menü veri tipi (API'den gelecek menuDefinition buna uygun olacak)
interface QRMenu {
  id: string;
  name: string;
  description?: string;
  branchId?: string | null;
  branchName?: string; // Bu bilgi restaurantInfo'dan gelebilir veya API'de birleştirilebilir
  products?: string[]; // Genel ürün ID'leri (eski yapı için)
  template?: string; // Ana template bilgisi, settings içinde de olabilir
  menus?: { // Alt menüler (eski yapı için)
    [key: string]: {
      id: string;
      name: string;
      products?: string[];
      imageUrl?: string;
    }
  };
  settings?: {
    primaryColor?: string;
    secondaryColor?: string;
    fontFamily?: string;
    showPrices?: boolean;
    showImages?: boolean;
    currencySymbol?: string;
    logoUrl?: string; // Bu restaurantInfo.logoUrl ile aynı mı?
    appearance?: {
      template?: string;
      cardStyle?: string;
      primaryColor?: string;
      secondaryColor?: string;
      fontFamily?: string;
      showPrices?: boolean;
      showImages?: boolean;
      showDescriptions?: boolean;
      showLogo?: boolean;
      currencySymbol?: string;
      menuLogoUrl?: string;
    },
    menus?: { // Alt menüler (yeni yapı, API'den bu gelecek)
        [key: string]: Menu; // Menu interface'i kullanılacak
    },
    products?: string[]; // Genel ürün ID'leri (yeni yapı)
  };
}

// Menü tipi (Alt menüler için)
interface Menu {
  id: string;
  name: string;
  products?: string[]; // Bu menüye ait ürün ID'leri
  imageUrl?: string;
}

interface RestaurantInfo {
  name: string;
  address?: string;
  phone?: string;
  logoUrl?: string;
  description?: string;
}

// API'den gelecek tüm ürünlerin detaylarını tutmak için state
let allFetchedProducts: MenuItem[] = [];

// CORS Hatasını önlemek için fetch yerine Image nesnesi kullanan bir bileşen
// Lazy loading özelliği de eklenmiş
const OptimizedImage = React.memo(({ url, alt, sx, height }: {
  url?: string;
  alt: string;
  sx: any;
  height?: number | string;
}) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const imgRef = React.useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(imgRef, { rootMargin: '200px' }); // Daha geniş görünürlük alanı
  const loadStartTimeRef = useRef<number>(0);
  const imgElementRef = useRef<HTMLImageElement | null>(null);
  
  // Resim önbellekte var mı kontrol et ve hızlıca ayarla
  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    
    // Önce önbellekten kontrol et - bu işlem senkron ve hızlı olmalı
    const cacheCheckStart = performance.now();
    const cached = getImageFromCache(url);
    const cacheCheckEnd = performance.now();
    
    if (cached.data && cached.isValid) {
      // Önbellekte geçerli bir resim varsa hemen kullan
      logPerformance(`Önbellekten resim yüklendi (${Math.round(cacheCheckEnd - cacheCheckStart)}ms): ${url.substring(0, 50)}...`);
      setImageSrc(cached.data);
      setLoading(false);
    }
  }, [url]); // Sadece url değiştiğinde çalışır
  
  // Resim yükleme işlemi için ayrı bir effect
  useEffect(() => {
    if (!url || (imageSrc && !loading)) {
      // Zaten yüklenmiş veya URL yok
      return;
    }
    
    // Eğer görünür değilse ve önbellekte yoksa, yüklemeyi başlatma
    if (!isVisible) {
      logPerformance(`Resim görünür değil, yükleme ertelendi: ${url.substring(0, 50)}...`);
      return;
    }
    
    // Önbellekte yoksa veya geçerli değilse yeni resmi yükle
    loadStartTimeRef.current = performance.now();
    logPerformance(`Resim yükleme başladı (${url.substring(0, 50)}...)`);
    
    // CORS hatasını önlemek için fetch yerine normal Image nesnesi kullanıyoruz
    const img = new Image();
    imgElementRef.current = img;
    
    // Önce loading=true yapma, eğer önbellekten geldiyse zaten false olacak
    if (!imageSrc) {
      setLoading(true);
    }
    
    img.onload = () => {
      const loadTime = performance.now() - loadStartTimeRef.current;
      logPerformance(`Resim yüklendi (${Math.round(loadTime)}ms): ${url.substring(0, 50)}...`);
      
      // Resmi önbelleğe kaydet
      saveImageToCache(url, url);
      setImageSrc(url);
      setLoading(false);
      
      // Resim yüklendikten sonra boyutunu ve diğer özelliklerini loglayalım
      logPerformance(`Resim boyutu: ${img.width}x${img.height} piksel`);
    };
    
    img.onerror = () => {
      const loadTime = performance.now() - loadStartTimeRef.current;
      logPerformance(`Resim yüklenemedi (${Math.round(loadTime)}ms): ${url.substring(0, 50)}...`);
      console.error('Resim yüklenemedi:', url);
      setError(true);
      setLoading(false);
    };
    
    // Opsiyonel: Resmin daha hızlı yüklenmesi için öncelik belirle
    if ('fetchPriority' in img) {
      (img as any).fetchPriority = 'high';
    }
    
    // Yüklemeyi başlat
    img.src = url;
    
  }, [url, isVisible, imageSrc, loading]);
  
  return (
    <Box ref={imgRef} sx={{ position: 'relative', ...sx, height }}>
      {loading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.05)'
        }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      {imageSrc && (
        <Box
          component="img"
          ref={(el: HTMLImageElement | null) => { imgElementRef.current = el; }}
          src={imageSrc}
          alt={alt}
          loading="eager" // Tarayıcı lazy loading yerine eager loading kullan
          decoding="async" // Asenkron çözme kullan
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: loading ? 'none' : 'block' // Yükleme sırasında gizle
          }}
          onLoad={() => {
            const loadedImg = imgElementRef.current;
            if (loadedImg) {
              logPerformance(`Resim DOM'a eklendi ve görüntüleniyor (${loadedImg.naturalWidth}x${loadedImg.naturalHeight}): ${url?.substring(0, 50)}...`);
            } else {
              logPerformance(`Resim DOM'a eklendi ve görüntüleniyor: ${url?.substring(0, 50)}...`);
            }
          }}
        />
      )}
      
      {error && !loading && (
        <Box sx={{ 
          height: '100%', 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.05)'
        }}>
          <Typography variant="body2" color="text.secondary">
            Resim Yüklenemedi
          </Typography>
        </Box>
      )}
    </Box>
  );
});

// Logo ve menü resimlerini optimize etmek için de aynı yaklaşımı kullanacağız
const OptimizedMenuImage = React.memo(({ url, alt, sx, variant = 'rounded' }: {
  url?: string;
  alt: string;
  sx: any;
  variant?: 'rounded' | 'square' | 'circular';
}) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const imgRef = React.useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(imgRef);
  
  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    
    // Önce önbellekten kontrol et
    const cached = getImageFromCache(url);
    
    if (cached.data && cached.isValid) {
      // Önbellekte geçerli bir resim varsa kullan
      setImageSrc(cached.data);
      setLoading(false);
      return;
    }
    
    // Eğer görünür değilse ve önbellekte yoksa, yüklemeyi başlatma
    if (!isVisible) return;
    
    // Önbellekte yoksa veya geçerli değilse yeni resmi yükle
    setLoading(true);
    
    // CORS hatasını önlemek için fetch yerine normal Image nesnesi kullanıyoruz
    const img = new Image();
    
    img.onload = () => {
      // Resmi önbelleğe kaydet - sadece URL'yi kaydedeceğiz
      saveImageToCache(url, url);
      setImageSrc(url);
      setLoading(false);
    };
    
    img.onerror = () => {
      console.error('Resim yüklenemedi:', url);
      setError(true);
      setLoading(false);
    };
    
    img.src = url;
    
  }, [url, isVisible]);
  
  return (
    <Box ref={imgRef} sx={sx}>
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          bgcolor: 'rgba(0,0,0,0.05)'
        }}>
          <Typography variant="body2" color="text.secondary">
            Resim Yüklenemedi
          </Typography>
        </Box>
      ) : (
        <Avatar 
          src={imageSrc}
          alt={alt}
          variant={variant}
          sx={{ width: '100%', height: '100%' }}
        />
      )}
    </Box>
  );
});

const QRMenuCustomerView: React.FC = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const navigate = useNavigate(); // Kullanılmıyorsa kaldırılabilir
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false); // Arka plan yenilemesi için
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<QRMenu | null>(null); // API'den gelen menuDefinition
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]); // Başlangıçta gösterilecek genel ürünler
  const [restaurantInfo, setRestaurantInfo] = useState<RestaurantInfo | null>(null);
  const [subMenus, setSubMenus] = useState<Menu[]>([]); // API'den gelen alt menüler
  const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);
  const [selectedMenuItems, setSelectedMenuItems] = useState<MenuItem[]>([]); // Seçili alt menünün ürünleri
  const [dataSource, setDataSource] = useState<'cache' | 'api' | null>(null); // Veri kaynağını izle
  
  const themeHook = useTheme(); // useTheme hook'unu burada çağırın
  const isMobile = useMediaQuery(themeHook.breakpoints.down('sm'));
  
  // Başlık stilini önce tanımlayalım
  const titleStyle = useMemo(() => ({
    color: menu?.settings?.appearance?.primaryColor || menu?.settings?.primaryColor || 'inherit',
    fontWeight: 'bold',
  }), [menu]);
  
  useEffect(() => {
    logPerformance('QRMenuCustomerView bileşeni yükleniyor');
    
    if (!menuId) {
      setError("Menü ID bulunamadı.");
      setLoading(false);
      return;
    }

    // Menü verilerini işleme fonksiyonu
    const processMenuData = (result: any, source: 'cache' | 'api') => {
      if (result.success && result.data) {
        const processStart = performance.now();
        const { menuDefinition, detailedProducts, restaurantInfo: resInfo } = result.data;
        
        logPerformance(`${source === 'cache' ? 'Önbellekten' : 'API\'den'} ${detailedProducts?.length || 0} ürün alındı`);
        setMenu(menuDefinition);
        
        // Tüm ürünleri önbellekten kontrol et
        const processProductsStart = performance.now();
        const processedProducts = detailedProducts.map((product: MenuItem) => {
          if (product.imageUrl) {
            const cached = getImageFromCache(product.imageUrl);
            if (cached.data && cached.isValid) {
              return { ...product, cachedImageUrl: cached.data };
            }
          }
          return product;
        });
        const processProductsEnd = performance.now();
        logPerformance(`Ürünler işlendi (${Math.round(processProductsEnd - processProductsStart)}ms)`);
        
        allFetchedProducts = processedProducts || []; // Gelen tüm ürünleri sakla
        setRestaurantInfo(resInfo);

        // Alt menüleri ayarla
        const subMenusStart = performance.now();
        const extractedSubMenus: Menu[] = Object.values(menuDefinition.settings?.menus || menuDefinition.menus || {});
        setSubMenus(extractedSubMenus);
        const subMenusEnd = performance.now();
        logPerformance(`Alt menüler işlendi (${Math.round(subMenusEnd - subMenusStart)}ms)`);

        // Başlangıçta gösterilecek genel ürünleri ayarla
        const menuItemsStart = performance.now();
        const generalProductIds = menuDefinition.settings?.products || menuDefinition.products || [];
        const generalItems = allFetchedProducts.filter(p => generalProductIds.includes(p.id));
        setMenuItems(generalItems);
        const menuItemsEnd = performance.now();
        logPerformance(`Menü öğeleri filtrelendi (${Math.round(menuItemsEnd - menuItemsStart)}ms)`);
        
        const processEnd = performance.now();
        logPerformance(`Tüm veriler işlendi (${Math.round(processEnd - processStart)}ms)`);
        setDataSource(source);
        
        // API'den gelen verileri önbelleğe kaydet (sadece API'den geldiyse)
        if (source === 'api') {
          saveMenuToCache(menuId, result);
        }
      } else {
        if (source === 'api') {
          // Eğer API'den hata geldiyse ve önbellekten veri yüklenmediyse hata göster
          if (dataSource !== 'cache') {
            setError(result.error || "API\'den beklenen formatta veri gelmedi.");
          }
        }
      }
    };

    // Önce önbellekten veri yüklemeyi dene
    const fetchCachedData = () => {
      const cacheCheckStart = performance.now();
      const cached = getMenuFromCache(menuId);
      const cacheCheckEnd = performance.now();
      
      if (cached.data && cached.isValid) {
        logPerformance(`Önbellekten menü verileri yüklendi (${Math.round(cacheCheckEnd - cacheCheckStart)}ms)`);
        processMenuData(cached.data, 'cache');
        setLoading(false);
        
        // Arka planda güncel verileri çek
        setBackgroundLoading(true);
        fetchApiData(true);
      } else {
        // Önbellekte veri yoksa API'den çek
        fetchApiData(false);
      }
    };

    // API'den veri çekme
    const fetchApiData = async (isBackground: boolean = false) => {
      if (!isBackground) {
        setLoading(true);
        setError(null);
      }
      
      const fetchStart = performance.now();
      logPerformance(`${isBackground ? 'Arka planda' : ''} API verisi çekme başladı`);
      
      try {
        const response = await nodeApi.get(`/api/public/qr-menu/${menuId}`);
        const fetchEnd = performance.now();
        logPerformance(`API yanıtı alındı (${Math.round(fetchEnd - fetchStart)}ms)`);
        
        const parseStart = performance.now();
        const result = response.data;
        const parseEnd = performance.now();
        logPerformance(`JSON parse edildi (${Math.round(parseEnd - parseStart)}ms)`);

        processMenuData(result, 'api');
      } catch (err: any) {
        console.error("Menü verileri API'den yüklenirken hata:", err);
        if (!isBackground || dataSource !== 'cache') {
          const errorMsg = err.response?.data?.error || err.message || "Menü verileri yüklenirken bir hata oluştu.";
          setError(errorMsg);
        }
      } finally {
        if (!isBackground) {
          setLoading(false);
        }
        setBackgroundLoading(false);
        const fetchTotalEnd = performance.now();
        logPerformance(`Toplam veri çekme ve işleme süresi: ${Math.round(fetchTotalEnd - fetchStart)}ms`);
      }
    };

    // İlk yükleme - önce önbellekten, sonra API'den
    fetchCachedData();
  }, [menuId]);
  
  // Alt menüyü seçme fonksiyonunu useCallback ile optimize edelim
  const handleMenuSelect = useCallback((currentSelectedMenu: Menu) => {
    logPerformance(`Alt menü seçildi: ${currentSelectedMenu.name}`);
    const startTime = performance.now();
    
    setSelectedMenu(currentSelectedMenu);
    
    if (currentSelectedMenu && currentSelectedMenu.products && currentSelectedMenu.products.length > 0) {
      const menuProductItems = allFetchedProducts.filter(p => 
        currentSelectedMenu.products?.includes(p.id)
      );
      logPerformance(`Alt menü ürünleri filtrelendi, ${menuProductItems.length} ürün bulundu (${Math.round(performance.now() - startTime)}ms)`);
      
      // Ürünlerin resim URL'lerini log'a kaydedelim
      const imageUrls = menuProductItems.filter(item => item.imageUrl).map(item => item.imageUrl);
      logPerformance(`Alt menüde ${imageUrls.length} adet resim URL'si bulundu`);
      
      // Önbellekte olan ve olmayan resimleri sayalım
      let cachedImages = 0;
      let uncachedImages = 0;
      
      imageUrls.forEach(url => {
        if (url) {
          const cached = getImageFromCache(url);
          if (cached.data && cached.isValid) {
            cachedImages++;
          } else {
            uncachedImages++;
          }
        }
      });
      
      logPerformance(`Alt menü resim durumu: ${cachedImages} önbellekte, ${uncachedImages} önbellekte değil`);
      
      setSelectedMenuItems(menuProductItems);
    } else {
      logPerformance(`Seçilen alt menüde ürün bulunamadı`);
      setSelectedMenuItems([]);
    }
  }, []);
  
  // Ana menüye dönme fonksiyonunu useCallback ile optimize edelim
  const handleBackToMainMenu = useCallback(() => {
    logPerformance('Ana menüye dönülüyor');
    const startTime = performance.now();
    
    setSelectedMenu(null);
    setSelectedMenuItems([]);
    // Genel ürünleri tekrar göstermek için menuItems'ı güncelleyebiliriz (eğer değişmişse)
    if (menu) {
      const generalProductIds = menu.settings?.products || menu.products || [];
      const generalItems = allFetchedProducts.filter(p => generalProductIds.includes(p.id));
      logPerformance(`Ana menüye dönüş tamamlandı, ${generalItems.length} ürün filtrelendi (${Math.round(performance.now() - startTime)}ms)`);
      setMenuItems(generalItems);
    } else {
      logPerformance(`Ana menüye dönüş tamamlandı (${Math.round(performance.now() - startTime)}ms)`);
    }
  }, [menu]);
  
  // Seçilen şablonu al
  const getSelectedTemplate = (): string => {
    if (menu?.settings?.appearance?.template) {
      return menu.settings.appearance.template;
    }
    return 'standard'; // Varsayılan şablon
  };
  
  // Seçilen kart stilini al
  const getCardStyle = (): string => {
    if (menu?.settings?.appearance?.cardStyle) {
      return menu.settings.appearance.cardStyle;
    }
    return 'standard'; // Varsayılan kart stili
  };
  
  // Para birimi sembolü
  const getCurrencySymbol = (): string => {
    const currencySymbol = menu?.settings?.appearance?.currencySymbol || 'TRY';
    
    switch(currencySymbol) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'TRY':
      default: return '₺';
    }
  };
  
  // Fiyat formatı
  const formatPrice = (price: number): string => {
    const symbol = getCurrencySymbol();
    
    // Para birimi sembolüne göre format değişebilir
    switch(menu?.settings?.appearance?.currencySymbol) {
      case 'USD': return `${symbol}${price.toFixed(2)}`;
      case 'EUR': return `${price.toFixed(2)} ${symbol}`;
      case 'GBP': return `${symbol}${price.toFixed(2)}`;
      case 'TRY':
      default: return `${price.toFixed(2)} ${symbol}`;
    }
  };
  
  // Şablona göre menü kartı stilini belirle
  const getMenuCardStyle = (template: string) => {
    if (template === 'list' || template === 'mixed') {
      return {
        cursor: 'pointer',
        borderRadius: 1,
        p: 0,
        mb: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2,
          bgcolor: 'rgba(0,0,0,0.01)'
        }
      };
    } else if (template === 'modern') {
      return {
        height: '100%', 
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '16px', // Daha yuvarlak köşeler
        transition: 'all 0.3s ease',
        boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
        },
        background: 'linear-gradient(to right bottom, #ffffff, #f8f9fa)' // Hafif gradient arka plan
      };
    }
    
    // standard veya diğer durumlar
    return {
      height: '100%', 
      cursor: 'pointer',
      overflow: 'hidden',
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    };
  };
  
  // Menü görünümü için stil seçici
  const shouldUseListMenuView = (template: string) => {
    return template === 'list' || template === 'mixed';
  };
  
  // Ürün görünümü için stil seçici
  const shouldUseListProductView = (template: string) => {
    return template === 'list'; // Sadece liste şablonu için liste ürün görünümü
  };
  
  // Ürün kartı stilini belirle
  const getProductCardStyle = (template: string) => {
    const cardStyle = getCardStyle();
    
    if (template === 'list') {
      return {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center', 
        borderRadius: cardStyle === 'minimal' ? 0 : 1,
        mb: 1,
        p: cardStyle === 'compact' ? 0.5 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: cardStyle !== 'minimal' ? 1 : 0,
          bgcolor: 'rgba(0,0,0,0.01)'
        }
      };
    } else if (template === 'modern') {
      return {
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: cardStyle === 'minimal' ? '0px' : 
                     cardStyle === 'compact' ? '8px' : '12px',
        boxShadow: cardStyle === 'minimal' ? 0 : '0 4px 15px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
        position: 'relative',
        '&:hover': {
          transform: cardStyle !== 'minimal' ? 'translateY(-5px) scale(1.02)' : 'none',
          boxShadow: cardStyle === 'minimal' ? 0 : '0 8px 20px rgba(0,0,0,0.12)'
        }
      };
    }
    
    // standard veya diğer durumlar
    return {
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: cardStyle === 'minimal' ? 0 : 
                   cardStyle === 'compact' ? 1 : 2,
      boxShadow: cardStyle === 'minimal' ? 0 : 1,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: cardStyle !== 'minimal' ? 'translateY(-4px)' : 'none',
        boxShadow: cardStyle === 'minimal' ? 0 : 3
      }
    };
  };
  
  // Ürün resmi stilini belirle
  const getProductImageStyle = (template: string) => {
    if (template === 'list') {
      return {
        width: 60,
        height: 60,
        borderRadius: 1,
        mr: 2,
        objectFit: 'cover' as 'cover',
        backgroundColor: '#f0f0f0'
      };
    } else if (template === 'modern') {
      return {
        objectFit: 'cover' as 'cover',
        backgroundColor: '#f0f0f0',
        transition: 'transform 0.5s',
        '&:hover': {
          transform: 'scale(1.05)'
        }
      };
    }
    
    // standard veya diğer durumlar
    return {
      objectFit: 'cover' as 'cover',
      backgroundColor: '#f0f0f0'
    };
  };
  
  // Ürün içerik stilini belirle
  const getProductContentStyle = (template: string) => {
    if (template === 'list') {
      return {
        flexGrow: 1,
        p: 0,
        pr: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
      };
    } else if (template === 'modern') {
      return {
        flexGrow: 1,
        p: 2.5, // Daha fazla iç boşluk
        position: 'relative',
        zIndex: 1
      };
    }
    
    // standard veya diğer durumlar
    return {
      flexGrow: 1,
      p: 2
    };
  };
  
  // Fiyat stili
  const getPriceStyle = (template: string, item: MenuItem) => {
    if (template === 'list') {
      return {
        fontWeight: 'bold',
        color: 'primary.main',
        whiteSpace: 'nowrap',
        ml: 2
      };
    } else if (template === 'modern') {
      return {
        position: 'absolute',
        top: 12,
        right: 12,
        bgcolor: 'primary.main', 
        color: 'white',
        py: 0.5,
        px: 1.5,
        borderRadius: 5,
        fontWeight: 'bold',
        fontSize: '0.875rem',
        boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
        zIndex: 2,
        opacity: item.isAvailable ? 1 : 0.6
      };
    }
    
    // standard veya diğer durumlar için boş nesne değil, varsayılan değerler döndür
    return {
      fontWeight: 'bold',
      color: 'primary.main'
    };
  };
  
  // Render performansını izlemek için useEffect
  useEffect(() => {
    const renderEnd = performance.now();
    logPerformance('QRMenuCustomerView render tamamlandı');
    
    return () => {
      logPerformance('QRMenuCustomerView bileşeni kaldırıldı');
    };
  }, []);
  
  // Şablon, kart stili, para birimi vb. hesaplamalarını useMemo ile optimize edelim
  const selectedTemplate = useMemo(() => getSelectedTemplate(), [menu]);
  const cardStyle = useMemo(() => getCardStyle(), [menu]);
  const currencySymbol = useMemo(() => getCurrencySymbol(), [menu]);
  
  // Menü kartı stilini useMemo ile optimize edelim
  const menuCardStyle = useMemo(() => getMenuCardStyle(selectedTemplate), [selectedTemplate]);
  
  // Görünüm seçimlerini useMemo ile optimize edelim
  const useListMenuView = useMemo(() => shouldUseListMenuView(selectedTemplate), [selectedTemplate]);
  const useListProductView = useMemo(() => shouldUseListProductView(selectedTemplate), [selectedTemplate]);
  
  // Ürün kartı stilini useMemo ile optimize edelim
  const productCardStyle = useMemo(() => getProductCardStyle(selectedTemplate), [selectedTemplate]);
  
  // Ürün resmi stilini useMemo ile optimize edelim
  const productImageStyle = useMemo(() => getProductImageStyle(selectedTemplate), [selectedTemplate]);
  
  // Ürün içerik stilini useMemo ile optimize edelim
  const productContentStyle = useMemo(() => getProductContentStyle(selectedTemplate), [selectedTemplate]);
  
  // Restorant bilgileri gösterim bileşeni
  const RestaurantInfoSection = useMemo(() => {
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, textAlign: 'center' }}>
        {/* Logo Gösterimi */}
        {restaurantInfo?.logoUrl && menu?.settings?.appearance?.showLogo !== false && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <OptimizedMenuImage
              url={restaurantInfo.logoUrl}
              alt={restaurantInfo.name}
              sx={{ width: 120, height: 120 }}
            />
          </Box>
        )}
        
        <Typography 
          variant="h5" 
          gutterBottom
          sx={titleStyle}
        >
          {menu?.name || 'Menü'}
        </Typography>
        
        {menu?.description && (
          <Typography variant="body2" color="text.secondary">
            {menu.description}
          </Typography>
        )}
      </Paper>
    );
  }, [restaurantInfo, menu, titleStyle]);
  
  // Menü logosu gösterim bileşeni
  const MenuLogoSection = useMemo(() => {
    if (!menu?.settings?.appearance?.menuLogoUrl || menu?.settings?.appearance?.showLogo === false) {
      return null;
    }
    
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <Box
          component={OptimizedImage}
          url={menu.settings.appearance.menuLogoUrl}
          alt={`${menu?.name || 'Menü'} Logosu`}
          sx={{
            width: isMobile ? '100%' : 'auto',
            maxWidth: isMobile ? '100%' : 250, 
            maxHeight: isMobile ? '30vh' : 250,
            objectFit: 'contain' as 'contain',
            boxShadow: isMobile ? 0 : 3,
            borderRadius: isMobile ? 0 : themeHook.shape.borderRadius,
          }}
          height="auto"
        />
      </Box>
    );
  }, [menu, isMobile, themeHook.shape.borderRadius]);
  
  // Özel tema oluştur
  const customTheme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: menu?.settings?.appearance?.primaryColor || menu?.settings?.primaryColor || '#1976d2',
      },
    },
    typography: {
      fontFamily: menu?.settings?.appearance?.fontFamily || menu?.settings?.fontFamily || 'Roboto',
    },
    components: {
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: menu?.settings?.appearance?.primaryColor || menu?.settings?.primaryColor || '#1976d2',
          },
        },
      },
    },
  }), [menu]);
  
  // Ürün Render Bileşeni
  const ProductItem = React.memo(({ item }: { item: MenuItem }) => {
    const priceStyle = useMemo(() => getPriceStyle(selectedTemplate, item), [selectedTemplate, item]);
    const startRenderTime = useRef(performance.now());
    
    useEffect(() => {
      logPerformance(`Ürün bileşeni render başladı: ${item.name}`);
      startRenderTime.current = performance.now();
      
      return () => {
        const renderTime = performance.now() - startRenderTime.current;
        logPerformance(`Ürün bileşeni kaldırıldı: ${item.name} (${Math.round(renderTime)}ms)`);
      };
    }, [item.name]);
    
    return (
      <Card elevation={0} sx={productCardStyle as any} onLoad={() => {
        const renderTime = performance.now() - startRenderTime.current;
        logPerformance(`Ürün kartı yüklendi: ${item.name} (${Math.round(renderTime)}ms)`);
      }}> 
        {selectedTemplate === 'modern' && menu?.settings?.appearance?.showPrices !== false && (
          <Box sx={priceStyle as any}> 
            {formatPrice(item.price)}
          </Box>
        )}
        
        {item.imageUrl && menu?.settings?.appearance?.showImages !== false ? (
          <OptimizedImage
            url={item.imageUrl}
            alt={item.name}
            sx={productImageStyle as any}
            height={isMobile ? '140px' : '180px'}
          />
        ) : (
          menu?.settings?.appearance?.showImages !== false && (
          <Box sx={{ 
            height: isMobile ? 140 : 180, 
            bgcolor: '#f0f0f0', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              Resim Yok
            </Typography>
          </Box>
          )
        )}
        
        <CardContent sx={productContentStyle as any}> 
          <Typography variant="h6" component="div" gutterBottom noWrap>
            {item.name}
          </Typography>
          
          {item.description && menu?.settings?.appearance?.showDescriptions !== false && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '40px'
              }}
            >
              {item.description}
            </Typography>
          )}
          
          <Box sx={{ mt: 'auto' }}>
            {selectedTemplate !== 'modern' && menu?.settings?.appearance?.showPrices !== false && (
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                {formatPrice(item.price)}
              </Typography>
            )}
            
            {!item.isAvailable && (
              <Chip
                label="Tükendi"
                color="error"
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  });
  
  // Menü Öğesi Render Bileşeni
  const MenuListItem = React.memo(({ subMenu }: { subMenu: Menu }) => {
    return (
      <Card 
        key={subMenu.id}
        sx={menuCardStyle as any} 
        onClick={() => handleMenuSelect(subMenu)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
          {subMenu.imageUrl ? (
            <OptimizedMenuImage
              url={subMenu.imageUrl}
              alt={subMenu.name}
              sx={{ width: 60, height: 60, mr: 2 }}
            />
          ) : (
            <Avatar 
              variant="rounded"
              sx={{ width: 60, height: 60, mr: 2, bgcolor: 'grey.200' }}
            >
              <RestaurantMenuIcon sx={{ color: 'primary.main' }} />
            </Avatar>
          )}
          <Typography variant="h6">{subMenu.name}</Typography>
          <Box sx={{ ml: 'auto' }}>
            <IconButton size="small" color="primary">
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>
    );
  });
  
  // Menü Grid Öğesi Render Bileşeni
  const MenuGridItem = React.memo(({ subMenu }: { subMenu: Menu }) => {
    return (
      <Grid item xs={12} sm={6} md={4} key={subMenu.id}>
        <Card 
          sx={menuCardStyle as any}
          onClick={() => handleMenuSelect(subMenu)}
        >
          {subMenu.imageUrl ? (
            <OptimizedImage
              url={subMenu.imageUrl}
              alt={subMenu.name}
              sx={{
                height: 180,
                ...(productImageStyle as any)
              }}
              height={180}
            />
          ) : (
            <Box sx={{ 
              height: 120, 
              bgcolor: '#f0f0f0', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <RestaurantMenuIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
            </Box>
          )}
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <RestaurantMenuIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="div">
                {subMenu.name}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  });
  
  // Yükleniyor durumu
  if (loading) {
    logPerformance('Yükleme ekranı gösteriliyor');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Menü yükleniyor...
        </Typography>
      </Box>
    );
  }
  
  // Hata durumu
  if (error) {
    logPerformance('Hata ekranı gösteriliyor');
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ 
        height: '100vh', // Sayfanın tamamının yüksekliğini ekranla sınırlar
        minHeight: '100vh', 
        bgcolor: '#f5f5f5',
        overflowY: 'auto', // Dikey kaydırmayı etkinleştirir
        '&::-webkit-scrollbar': { // Özel scrollbar stilleri
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.05)',
          borderRadius: '8px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: menu?.settings?.appearance?.primaryColor 
            ? `${menu.settings.appearance.primaryColor}40` // %25 opaklık
            : menu?.settings?.primaryColor 
              ? `${menu.settings.primaryColor}40`
              : 'rgba(0,0,0,0.2)',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: menu?.settings?.appearance?.primaryColor 
              ? `${menu.settings.appearance.primaryColor}80` // %50 opaklık
              : menu?.settings?.primaryColor
                ? `${menu.settings.primaryColor}80`
                : 'rgba(0,0,0,0.3)',
          }
        },
        pr: 1 // Scrollbar için sağdan boşluk
      }}>
        {backgroundLoading && (
          <Box 
            sx={{ 
              position: 'fixed', 
              top: 10, 
              right: 10, 
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.9)',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption">Güncelleniyor...</Typography>
          </Box>
        )}
        
        {dataSource === 'cache' && !backgroundLoading && (
          <Box 
            sx={{ 
              position: 'fixed', 
              top: 10, 
              right: 10, 
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.9)',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            <Typography variant="caption">Önbellekten yüklendi</Typography>
          </Box>
        )}
        
        {/* Menü İçeriği */}
        <Container maxWidth="lg" sx={{ pt: 2, pb: 8 // Alt boşluğu artırdık
            // Bu Container artık kendi başına kaymayacak
        }}>
          {!selectedMenu ? (
            <>
              {RestaurantInfoSection}
              
              {MenuLogoSection}
              
              {/* İçerik Alanı (Artık kendi başına kaymayacak) */}
              <Box 
                sx={{ 
                  // height: 'calc(100vh - 140px - 70px)', // KALDIRILDI
                  // overflowY: 'auto', // KALDIRILDI
                  // Aşağıdaki scrollbar stilleri de artık burada etkili olmayacak
                  // '&::-webkit-scrollbar': {
                  //   width: '8px',
                  // },
                  // '&::-webkit-scrollbar-track': {
                  //   backgroundColor: 'rgba(0,0,0,0.05)',
                  //   borderRadius: '8px'
                  // },
                  // '&::-webkit-scrollbar-thumb': {
                  //   backgroundColor: menu?.settings?.appearance?.primaryColor 
                  //     ? `${menu.settings.appearance.primaryColor}40` 
                  //     : menu?.settings?.primaryColor 
                  //       ? `${menu.settings.primaryColor}40`
                  //       : 'rgba(0,0,0,0.2)',
                  //   borderRadius: '8px',
                  //   '&:hover': {
                  //     backgroundColor: menu?.settings?.appearance?.primaryColor 
                  //       ? `${menu.settings.appearance.primaryColor}80` 
                  //       : menu?.settings?.primaryColor
                  //         ? `${menu.settings.primaryColor}80`
                  //         : 'rgba(0,0,0,0.3)',
                  //   }
                  // },
                  // pr: 1 // Bu kalabilir veya duruma göre ayarlanabilir
                }}
              >
                {/* Menü Listesi (Alt Menüler) */}
                {subMenus.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      textAlign: 'center', 
                      mb: 2,
                      color: menu?.settings?.appearance?.primaryColor || menu?.settings?.primaryColor || 'inherit',
                      fontFamily: 'inherit',
                      fontWeight: 'bold'
                    }}>
                      Menülerimiz
                    </Typography>
                    
                    {useListMenuView ? (
                      // Liste görünümünde menüler
                      <Box>
                        {subMenus.map((subMenu) => (
                          <MenuListItem key={subMenu.id} subMenu={subMenu} />
                        ))}
                      </Box>
                    ) : (
                      // Standart ve modern görünümde menüler
                      <Grid container spacing={2}>
                        {subMenus.map((subMenu) => (
                          <MenuGridItem key={subMenu.id} subMenu={subMenu} />
                        ))}
                      </Grid>
                    )}
                  </Box>
                ) : null}
                
                {/* Genel Ürün Listesi - Sadece ürün varsa göster (menuItems state'i kullanılır) */}
                {menuItems.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    {subMenus.length > 0 && (
                      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                        Tüm Ürünlerimiz
                      </Typography>
                    )}
                    
                    {useListProductView ? (
                      // Liste görünümünde ürünler
                      <Box>
                        {menuItems.map(item => (
                          <ProductItem key={item.id} item={item} />
                        ))}
                      </Box>
                    ) : (
                      // Standart ve modern görünümde ürünler
                      <Grid container spacing={2}>
                        {menuItems.map(item => (
                          <Grid item xs={6} sm={4} md={3} key={item.id}>
                            <ProductItem item={item} />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <>
              {/* Seçilen Menü Başlığı */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton 
                  onClick={handleBackToMainMenu} 
                  sx={{ mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={titleStyle}>
                  {selectedMenu.name}
                </Typography>
              </Box>
              
              {/* İçerik Alanı (Artık kendi başına kaymayacak) */}
              <Box 
                sx={{ 
                  // height: 'calc(100vh - 140px)', // KALDIRILDI
                  // overflowY: 'auto', // KALDIRILDI
                  // Aşağıdaki scrollbar stilleri de artık burada etkili olmayacak
                }}
              >
                {useListProductView ? (
                  <Box>
                    {selectedMenuItems.length > 0 ? (
                      selectedMenuItems.map(item => (
                        <ProductItem key={item.id} item={item} />
                      ))
                    ) : (
                      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                        <Typography color="text.secondary">
                          Bu menüde henüz ürün bulunmuyor.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {selectedMenuItems.length > 0 ? (
                      selectedMenuItems.map(item => (
                        <Grid item xs={6} sm={4} md={3} key={item.id}>
                          <ProductItem item={item} />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                          <Typography color="text.secondary">
                            Bu menüde henüz ürün bulunmuyor.
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default React.memo(QRMenuCustomerView); 